import { Component, computed, inject, input } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { KENDO_STACKLAYOUT } from '@progress/kendo-angular-layout';
import { AbstractManageComponent } from '@unity/abstract/abstract-manage.component';
import { AgencyUserDetailedDto, AgencyUsersClient, TransitionRequestDto, UserUpdateDto } from '@unity/clients';
import { AuthorizeDirective } from '@unity/directives/authorize.directive';
import { DropdownAgencyUserRoleControl } from '@unity/forms/dropdown-user-role-control/dropdown-agency-user-role-control';
import { TextboxReadonlyControlComponent } from '@unity/forms/textbox-readonly-control/textbox-readonly-control.component';
import { FormLayoutComponent } from '@unity/layouts/form-layout/form-layout.component';
import { PageContainerComponent } from '@unity/layouts/page-container/page-container.component';
import { ActionSharedComponent } from '@unity/shared/composed/action-shared/action-shared.component';
import { PanelManageSharedComponent } from '@unity/shared/composed/panel-manage-shared/panel-manage-shared.component';
import { GridAgreementSharedComponent } from '@unity/shared/grid-agreement-shared/grid-agreement-shared.component';
import { WorkflowSelectorSharedComponent } from '@unity/shared/workflow-selector-shared/workflow-selector-shared.component';
import { emailFormControl, nonZeroFormControl, stringFormControl } from '@unity/utils/form-controls';
import { QuickActions } from '@unity/utils/quick-actions';
import { FormGroupControlSchema, UnityEntitlementPath } from '@unity/utils/types';
import { EMPTY } from 'rxjs';

class UserUpdateSchema implements FormGroupControlSchema<UserUpdateDto> {
  public full_name: FormControl<string>;
  public user_name: FormControl<string>;
  public role_id: FormControl<number>;

  public constructor(user?: AgencyUserDetailedDto) {
    this.full_name = stringFormControl({ disabled: true, value: user?.full_name });
    this.user_name = emailFormControl({ disabled: true, value: user?.user_name });
    this.role_id = nonZeroFormControl({ disabled: true, value: user?.role?.id });
  }
}

@Component({
  selector: 'unity-manage-agency-user-page',
  standalone: true,
  imports: [
    KENDO_STACKLAYOUT,
    ReactiveFormsModule,
    PanelManageSharedComponent,
    PageContainerComponent,
    TextboxReadonlyControlComponent,
    AuthorizeDirective,
    GridAgreementSharedComponent,
    FormLayoutComponent,
    DropdownAgencyUserRoleControl,
    WorkflowSelectorSharedComponent,
    ActionSharedComponent,
  ],
  templateUrl: './manage-agency-user-page.component.html',
  styleUrl: './manage-agency-user-page.component.scss',
})
export class ManageAgencyUserPageComponent extends AbstractManageComponent<AgencyUserDetailedDto, UserUpdateDto, UserUpdateSchema> {
  private readonly agencyUsersClient = inject(AgencyUsersClient);

  public readonly $agencyId = input.required({ alias: 'agencyId', transform: (value: string) => parseInt(value) });
  public readonly $userId = input.required({ alias: 'userId', transform: (value: string) => parseInt(value) });

  protected override mainResource = rxResource({
    params: () => {
      const agencyId = this.$agencyId();
      const resourceId = this.$resourceId();
      return resourceId && agencyId ? { agencyId, resourceId } : undefined;
    },
    stream: ({ params: { agencyId, resourceId } }) => this.agencyUsersClient.getOneAsDetailed(resourceId, agencyId),
  });

  protected override updateResourceFn$ = (_: unknown, __: unknown) => EMPTY;
  protected override deleteResourceFn$ = (_: number) => EMPTY;
  protected override availableTransitionsFn$ = (resourceId: number) => this.availableTransitionsClient.getAllForUserAsMini(resourceId);

  protected override doTransitionFn$ = (resourceId: number, transition: TransitionRequestDto) =>
    this.transitionsClient.setForUser(resourceId, transition);

  protected override newControlsFn(dto?: AgencyUserDetailedDto | undefined): UserUpdateSchema {
    return new UserUpdateSchema(dto);
  }

  protected override editEntitlement: UnityEntitlementPath = 'User:Update';
  protected override transitionEntitlement: UnityEntitlementPath = 'User:Transition';
  protected override deleteEntitlement: UnityEntitlementPath = 'User:Delete';
  protected override resourceName = 'User';
  protected override $resourceId = this.$userId;

  protected override readonly $contentHeader = computed(() => `User: ${this.$resource()?.full_name}`);
  protected returnToDashboardAction = QuickActions.ReturnToDashboard;
}
